import React, { useEffect, useState } from "react";
import { Application } from "../../../candidate/jobApplications/model/JobApplicationsModel";
import { JobApplication } from "../model/JobModel";

interface ApplicationContactModalProps {
  application: JobApplication | null;
  isOpen: boolean;
  onClose: () => void;
  onSend: (message: string, subject: string) => void;
}

const ApplicationContactModal: React.FC<ApplicationContactModalProps> = ({
  application,
  isOpen,
  onClose,
  onSend,
}) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [sending, setSending] = useState(false);

  const templates = [
    {
      id: "interview",
      name: "Interview Invitation",
      subject: "Interview Opportunity at [Company Name]",
      message: `Hi ${application?.candidate_name?.split(" ")[0]},

  I hope this message finds you well. I came across your profile and was impressed by your background in ${
    application?.candidate.current_job_title || "your field"
  }.

  We have an exciting opportunity that I believe would be a great fit for your skills and experience. Would you be interested in scheduling a brief call to discuss this further?

  Looking forward to hearing from you.

  Best regards,
  [Your Name]`,
    },
    {
      id: "opportunity",
      name: "Job Opportunity",
      subject: "Exciting Career Opportunity",
      message: `Hello ${application?.candidate_name?.split(" ")[0]},

  I hope you're doing well. I'm reaching out because we have a fantastic opportunity that aligns perfectly with your experience in ${
    application?.candidate.current_job_title || "your field"
  }.

  The role offers excellent growth potential and competitive compensation. Would you be open to a quick conversation to learn more?

  Best regards,
  [Your Name]`,
    },
    {
      id: "networking",
      name: "Networking",
      subject: "Let's Connect",
      message: `Hi ${application?.candidate_name?.split(" ")[0]},

  I came across your profile and was impressed by your background. I'd love to connect and learn more about your experience in ${
    application?.candidate.current_job_title || "your field"
  }.

  Would you be interested in a brief coffee chat or call?

  Best regards,
  [Your Name]`,
    },
  ];
  useEffect(() => {
    if (isOpen) {
      setSubject("");
      setMessage("");
      setSelectedTemplate("");
    }
  }, [isOpen, application]);
  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setSubject(template.subject);
      setMessage(template.message);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      alert("Please fill in both subject and message");
      return;
    }

    setSending(true);
    try {
      await onSend(message, subject);
      onClose();
      // Reset form
      setSubject("");
      setMessage("");
      setSelectedTemplate("");
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-md shadow-lg flex flex-col max-h-[90vh]">
        <div className="p-5 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-lg font-semibold text-indigo-600">
                  {application?.candidate_name?.[0]}
                  {application?.candidate_name?.[1]}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Contact {application?.candidate_name}
                </h3>
                <p className="text-sm text-gray-500">
                  {application?.candidate.current_job_title}
                  {application?.candidate.current_company &&
                    ` at ${application?.candidate.current_company}`}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Contact Info */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4 text-sm">
              {" "}
              {application?.candidate_email && (
                <div className="flex items-center text-gray-600">
                  {" "}
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {" "}
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />{" "}
                  </svg>{" "}
                  {application?.candidate_email}{" "}
                </div>
              )}{" "}
              {application?.candidate.phone && (
                <div className="flex items-center text-gray-600">
                  {" "}
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {" "}
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />{" "}
                  </svg>{" "}
                  {application?.candidate.phone}{" "}
                </div>
              )}{" "}
            </div>{" "}
          </div>

          {/* Templates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Templates
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template.id)}
                  className={`p-3 text-left border rounded-lg text-sm hover:bg-gray-50 ${
                    selectedTemplate === template.id
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-200"
                  }`}
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter email subject"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter your message"
            />
          </div>
        </div>

        {/* ================= FOOTER (Fixed) ================= */}
        <div className="p-4 border-t flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !message.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationContactModal;
